import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { SalesBoard as SalesBoardData, SalesGroupCount } from "../../lib/sales-types";
import { relativeTime } from "../../lib/time";
import { useCallLinkState } from "../../lib/call-overlay";
import type { User } from "../../types";
import { CallRow, toneClass } from "../../components/sales/CallRow";
import { cn } from "../../components/ui/cn";

/**
 * The landing view.
 *
 * The brief gives this screen two jobs and ranks them, so the page is built in
 * that order and nothing is allowed above them:
 *
 *   1. **Make the neglected calls impossible to miss.** The needs-attention
 *      panel is second on the page, never collapsed, and split by *why* --
 *      nobody has rung them, versus somebody rang and let it go cold. Those are
 *      different failures with different fixes and a single "overdue" pile
 *      hides which one is happening.
 *   2. **Say what to ring next.** The worklist underneath is the hero: the
 *      calls this reader could actually pick up, in the server's ranked order,
 *      with a Claim button on every unclaimed row so the decision and the
 *      action are one movement.
 *
 *      *Could actually pick up* is doing work there. It was the whole open
 *      pool, other people's claimed calls included, which made the hero list
 *      partly a list of things the reader must not touch -- and a list you
 *      have to filter with your eyes before acting on is one you stop reading.
 *      It is now their own calls plus the unclaimed pool. Everything else is
 *      still one click away under By group and in the full list, so nothing is
 *      hidden, only moved off the one section that is meant to be a
 *      instruction rather than a report.
 *
 * Everything below those two is collapsed by default, per the brief. An
 * overdue call appears in the panel at the top *and* in its own group section
 * lower down, flagged there too -- that duplication is asked for by name, and
 * both places read from the same server response so they cannot disagree.
 */
const WORKLIST_ROWS = 15;

export function SalesBoard({ user }: { user: User }) {
  const client = useQueryClient();

  const board = useQuery({
    queryKey: queryKeys.salesBoard,
    queryFn: salesApi.board,
    staleTime: 30_000,
  });

  /* The hero list. A separate query from the board so it can be paged and
     filtered later without the counts above it refetching. */
  const worklist = useQuery({
    queryKey: queryKeys.salesCalls({
      status: "open",
      mineOrPool: true,
      limit: WORKLIST_ROWS,
    }),
    queryFn: () =>
      salesApi.calls({
        status: "open",
        mineOrPool: true,
        limit: WORKLIST_ROWS,
      }),
    staleTime: 15_000,
  });

  const claim = useMutation({
    mutationFn: (id: number) => salesApi.claim(id),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  /* The unclaim beside your own name. Same invalidation as claiming: letting a
     call go changes the pool count, your own count and the worklist order. */
  const release = useMutation({
    mutationFn: (id: number) => salesApi.release(id),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  /* Pinning reorders the queue for everybody, so it is a manager's, matching
     the route. Undefined for everyone else, which is what hides the control. */
  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: number; pinned: boolean }) =>
      salesApi.pin(id, pinned),
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.salesRoot }),
  });

  const onPin =
    user.role === "admin"
      ? (id: number, pinned: boolean) => pin.mutate({ id, pinned })
      : undefined;

  const kpi = board.data?.kpi;
  const me = board.data?.me;
  const attention = board.data?.attention;

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-y-auto p-3 sm:p-6">
      <header className="mb-4">
        <h1 className="text-lg font-semibold text-content">Today</h1>
      </header>

      {/*
        Two rows, and the order matters. A rep opening this wants to know how
        *they* are doing before how the desk is doing -- one row mixing the two
        means the big number on the page answers a question nobody asked. Both
        are always open.

        The two rows carry the same four tiles in the same four positions, so
        the eye can drop straight down from "mine" to "everyone's" and compare
        without re-reading the labels.
      */}
      <section className="mb-5">
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-faint">
          You
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
          <Kpi
            label="Assigned to me"
            value={me?.assigned}
            to="/sales/calls?mine=true"
          />
          {/*
            Attempts and answers, side by side.

            One "Calls today" tile counted conversations only, so a rep who
            logged two no-answers saw nought and reported it as broken. The
            figure was right and the label was wrong: counting conversations is
            a deliberate decision about what the desk is measured on, but a
            tile that does not move when you log a call reads as a bug and
            costs trust in the whole board. Both, named for what they are.
          */}
          <Kpi
            label="Attempts today"
            value={me?.attemptsToday}
            delta={me ? me.attemptsToday - me.attemptsYesterday : undefined}
            deltaTitle="Every attempt logged, against all of yesterday"
          />
          <Kpi
            label="Answered today"
            value={me?.answeredToday}
            to={`/sales/closed?mine=true&from=${today()}&to=${today()}`}
            delta={me ? me.answeredToday - me.answeredYesterday : undefined}
            deltaTitle="Spoke or asked for a callback, against all of yesterday"
          />
          <Kpi
            label="Answered this week"
            value={me?.answeredThisWeek}
            delta={me ? me.answeredThisWeek - me.answeredLastWeek : undefined}
            deltaTitle="Against the same point last week"
          />
          <RevenueTile windowDays={me?.revenue.windowDays} />
        </div>
      </section>

      <section className="mb-5">
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-faint">
          Total
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-5">
          <Kpi label="Open calls" value={kpi?.open} to="/sales/calls" />
          <Kpi
            label="Attempts today"
            value={kpi?.attemptsToday}
            delta={kpi ? kpi.attemptsToday - kpi.attemptsYesterday : undefined}
            deltaTitle="Every attempt logged, against all of yesterday"
          />
          {/*
            Through to the calls closed today, which is the list somebody
            reaching for this tile is after -- "we answered eleven, show me
            them". Dated rather than bare, so it lands on today's work instead
            of every call the desk has ever closed.

            The count and the list will not always match, and that is the
            measures talking rather than a bug: a call answered today can be
            left open for a callback, and one closed today may have been
            answered last week. The tile counts conversations; the list counts
            closures.
          */}
          <Kpi
            label="Answered today"
            value={kpi?.answeredToday}
            to={`/sales/closed?from=${today()}&to=${today()}`}
            delta={
              kpi ? kpi.answeredToday - kpi.answeredYesterday : undefined
            }
            deltaTitle="Spoke or asked for a callback, against all of yesterday"
          />
          <Kpi
            label="Answered this week"
            value={kpi?.answeredThisWeek}
            delta={
              kpi ? kpi.answeredThisWeek - kpi.answeredLastWeek : undefined
            }
            deltaTitle="Against the same point last week"
          />
          <RevenueTile windowDays={kpi?.revenue.windowDays} />
        </div>
      </section>

      {/* ── Who has rung what today. ───────────────────────────────────── */}
      <CallsByPerson
        data={board.data?.callsByPerson}
        currentUserId={user.id}
      />

      {/* ── Needs attention. Always open. ──────────────────────────────── */}
      <section className="mb-5 rounded-lg border border-line bg-surface">
        <header className="flex items-center gap-2 border-b border-line px-3 py-2">
          <h2 className="text-sm font-medium text-content">Needs attention</h2>
          {(attention?.total ?? 0) > 0 ? (
            <span className="rounded-full bg-red-600 px-1.5 text-[10px] font-medium text-white">
              {attention!.total}
            </span>
          ) : null}
          <Link
            to="/sales/calls?overdue=true"
            className="ml-auto text-xs text-brand-600 hover:underline"
          >
            See all
          </Link>
        </header>

        {board.isLoading ? (
          <p className="p-4 text-center text-xs text-faint">Loading…</p>
        ) : (attention?.total ?? 0) === 0 ? (
          <p className="p-4 text-center text-xs text-faint">
            Nothing is overdue. Every open call has been rung inside the rules.
          </p>
        ) : (
          /*
            Stacked full width, not three columns side by side.

            They were three narrow columns, which meant each row had about a
            third of the screen and everything in it bunched against both
            edges -- the same complaint as the calls list, for the same reason.
            A call row needs the full width to lay its six fields out, and
            these are the same rows.

            The cost is scrolling, and it is the right trade: this panel is
            worked through one call at a time, not compared across piles.
          */
          <div className="divide-y divide-line">
            <AttentionColumn
              title="Nobody has rung these"
              calls={attention!.neverAttempted}
              currentUserId={user.id}
              onClaim={claim.mutate}
              onRelease={release.mutate}
              onPin={onPin}
              claiming={claim.isPending ? claim.variables : undefined}
            />
            <AttentionColumn
              title="Going cold"
              calls={attention!.stale}
              currentUserId={user.id}
              onClaim={claim.mutate}
              onRelease={release.mutate}
              onPin={onPin}
              claiming={claim.isPending ? claim.variables : undefined}
            />
            {/* Past due only earns a section when there is one, so a desk that
                has not set due dates is not shown a permanently empty pile. */}
            {attention!.pastDue.length > 0 ? (
              <AttentionColumn
                title="Past due"
                calls={attention!.pastDue}
                currentUserId={user.id}
                onClaim={claim.mutate}
                onRelease={release.mutate}
                onPin={onPin}
                claiming={claim.isPending ? claim.variables : undefined}
              />
            ) : null}
          </div>
        )}
      </section>

      {claim.isError || release.isError ? (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {(claim.error ?? release.error) instanceof ApiError
            ? (claim.error ?? release.error)!.message
            : "That did not work."}
        </p>
      ) : null}

      {/* ── Work next. The hero. ───────────────────────────────────────── */}
      <section className="mb-5 rounded-lg border border-line bg-surface">
        <header className="flex items-center gap-2 border-b border-line px-3 py-2">
          <h2 className="text-sm font-medium text-content">Work next</h2>
          {/* Not on a phone, where it wrapped to three lines beside the
              heading and pushed "Full list" off the end of the row. */}
          <span className="hidden text-[11px] text-faint sm:inline">
            yours and the pool — pinned first, then overdue, then by group and
            how long it has sat
          </span>
          <Link
            to="/sales/calls"
            className="ml-auto text-xs text-brand-600 hover:underline"
          >
            Full list
          </Link>
        </header>

        {worklist.isLoading ? (
          <p className="p-4 text-center text-xs text-faint">Loading…</p>
        ) : (worklist.data?.calls.length ?? 0) === 0 ? (
          /*
            Two different empty states behind one blank list, and they mean
            opposite things. A quiet desk has nothing to ring; a busy one where
            every call is already claimed has plenty, none of it this reader's.
            `kpi.open` counts the whole pool, so it can tell them apart -- and
            the second case points at where the work actually is rather than
            inviting somebody to raise a call the desk does not need.
          */
          <p className="p-6 text-center text-xs text-faint">
            {(kpi?.open ?? 0) > 0
              ? "Nothing waiting for you — every open call is claimed by somebody else. See By group below."
              : "No open calls. Raise one with the button above."}
          </p>
        ) : (
          worklist.data!.calls.map((call) => (
            <CallRow
              key={call.id}
              call={call}
              currentUserId={user.id}
              onClaim={claim.mutate}
              onRelease={release.mutate}
              onPin={onPin}
              claiming={claim.isPending && claim.variables === call.id}
            />
          ))
        )}
      </section>

      {/* ── Per group. Collapsed. ──────────────────────────────────────── */}
      <section className="mb-5">
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-faint">
          By group
        </h2>
        {(board.data?.groups.length ?? 0) === 0 ? (
          <p className="rounded-lg border border-dashed border-line p-4 text-center text-xs text-faint">
            No open calls in any group.
          </p>
        ) : (
          board.data!.groups.map((group) => (
            <GroupSection
              key={String(group.id)}
              group={group}
              currentUserId={user.id}
              onClaim={claim.mutate}
              onRelease={release.mutate}
              onPin={onPin}
              claiming={claim.isPending ? claim.variables : undefined}
            />
          ))
        )}
      </section>

      {/* ── Closed lately. Collapsed. ──────────────────────────────────── */}
      <details className="mb-8 rounded-lg border border-line bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-content hover:bg-surface-2">
          Closed this week
          <span className="ml-2 text-[11px] font-normal text-faint">
            {kpi ? `${kpi.closedToday} today, ${kpi.closedThisWeek} this week` : ""}
          </span>
        </summary>

        <div className="border-t border-line">
          {(board.data?.scoreSplit.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-1.5 border-b border-line px-3 py-2">
              {board.data!.scoreSplit.map((score) => (
                <span
                  key={score.scoreId}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium",
                    toneClass(score.tone),
                  )}
                >
                  {score.label} · {score.total}
                </span>
              ))}
            </div>
          ) : null}

          {(board.data?.closedRecently.length ?? 0) === 0 ? (
            <p className="p-4 text-center text-xs text-faint">
              Nothing closed since Monday.
            </p>
          ) : (
            board.data!.closedRecently.map((call) => (
              <ClosedRow key={call.id} call={call} />
            ))
          )}
        </div>
      </details>
    </div>
  );
}

/**
 * One line of "Closed this week", with its note readable.
 *
 * Its own component only because the note has to be a button -- a button
 * inside an anchor is invalid markup and behaves differently in every browser,
 * so the link now covers the name and the note sits beside it rather than
 * inside it. Same reasoning, and the same expand-and-collapse behaviour, as
 * `ClosingNote` in `CallRow`; this panel is read the same way and had the same
 * complaint against it.
 */
function ClosedRow({
  call,
}: {
  call: SalesBoardData["closedRecently"][number];
}) {
  const [open, setOpen] = useState(false);
  /* Over the board, which stays put underneath -- see `call-overlay.ts`. */
  const linkState = useCallLinkState();

  return (
    <div className="flex items-start gap-3 border-b border-line px-5 py-2 text-xs last:border-b-0 hover:bg-surface-2">
      <span className="min-w-0 flex-1">
        <Link
          to={`/sales/calls/${call.id}`}
          state={linkState}
          className="block min-w-0"
        >
          <span className="block truncate text-content">
            {call.companyName}
            {call.subject ? (
              <span className="text-faint"> — {call.subject}</span>
            ) : null}
          </span>
        </Link>

        {/*
          What was written when it closed. A score alone says "Success" and
          nothing about why, and this panel is read to catch up on a week --
          the note is the catching up. One line at rest so eight of them still
          fit on a screen, all of it on a click, and back to one line on the
          next click.
        */}
        {call.closingNotes ? (
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-expanded={open}
            title={open ? "Hide the full note" : "Show the full note"}
            className={cn(
              "block w-full rounded text-left text-[11px] text-faint",
              "hover:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
              open ? "whitespace-pre-line" : "truncate",
            )}
          >
            {call.closingNotes}
          </button>
        ) : null}
      </span>

      <span
        className={cn(
          "shrink-0 self-start rounded-full px-2 py-0.5 text-[11px] font-medium",
          toneClass(call.scoreTone),
        )}
      >
        {call.scoreLabel ?? "Closed"}
      </span>
      <span className="hidden shrink-0 self-start text-faint sm:inline">
        {call.closedByName ?? "somebody"}
        {call.closedAt ? `, ${relativeTime(call.closedAt)}` : ""}
      </span>
    </div>
  );
}

/**
 * One tile.
 *
 * No description line. Four words under every number turned a row of four
 * tiles into sixteen lines to read, and "Calls today" does not need
 * explaining -- what it counts belongs in the tooltip, where somebody who
 * genuinely wonders can find it once.
 */
/**
 * Who has rung how many today, and in which group.
 *
 * A table rather than more tiles, because the question is comparative. "Have I
 * done enough" cannot be answered by your own number -- which the board
 * already shows twice -- only by the row above and below yours.
 *
 * Attempts beside the name, then answered calls by group and in total. The
 * attempts are their own column and not part of the total, as the desk asked:
 * side by side the two say how the day went -- forty rung, six answered --
 * where one number folded into the other said neither.
 *
 * Absent entirely when nobody has rung anything. An empty leaderboard first
 * thing in the morning is a table of dashes that says only "the day has not
 * started", and it would be the largest thing on the page while saying it.
 */
function CallsByPerson({
  data,
  currentUserId,
}: {
  data: SalesBoardData["callsByPerson"] | undefined;
  currentUserId: number;
}) {
  if (!data || data.people.length === 0) return null;

  return (
    <section className="mb-5 rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-medium text-content">Calls today, by person</h2>
        <span className="text-[11px] text-faint">
          groups and total count answered calls
        </span>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-faint">
            <tr>
              <th className="px-3 py-1.5 font-medium">Person</th>
              <th className="px-3 py-1.5 text-right font-medium" title="Every attempt logged, answered or not">
                Attempts
              </th>
              {data.groups.map((group) => (
                <th key={group.key} className="px-3 py-1.5 text-right font-medium">
                  {group.name}
                </th>
              ))}
              {/* Total last, where a total belongs, and the only bold column:
                  it is what the table is scanned down. */}
              <th className="px-3 py-1.5 text-right font-medium text-content">
                Total answered
              </th>
            </tr>
          </thead>

          <tbody>
            {data.people.map((person) => (
              <tr
                key={person.userId}
                className={cn(
                  "border-t border-line",
                  /* Your own row marked, so the comparison has a fixed point.
                     Finding yourself in a list of eight names is the thing you
                     do first and every time. */
                  person.userId === currentUserId && "bg-brand-50/60 dark:bg-brand-950/30",
                )}
              >
                <td className="px-3 py-1.5 text-xs text-content">
                  {person.name}
                  {person.userId === currentUserId ? (
                    <span className="ml-1.5 text-[10px] text-faint">you</span>
                  ) : null}
                </td>

                <td className="px-3 py-1.5 text-right text-xs tabular-nums text-muted">
                  {person.attempts}
                </td>

                {data.groups.map((group) => (
                  <td
                    key={group.key}
                    className="px-3 py-1.5 text-right text-xs tabular-nums text-muted"
                  >
                    {/* A dash, not a nought. Nought reads as a result; this
                        person simply has not touched that group today. */}
                    {person.byGroup[group.key] ?? <span className="text-faint">—</span>}
                  </td>
                ))}

                <td className="px-3 py-1.5 text-right text-xs font-medium tabular-nums text-content">
                  {person.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Today, as the `YYYY-MM-DD` the closed list's date inputs speak.
 *
 * Built from the local parts rather than sliced off `toISOString()`, which is
 * UTC: for the hour before midnight in BST that would send a rep to tomorrow's
 * empty list.
 */
function today(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function Kpi({
  label,
  value,
  to,
  delta,
  deltaTitle,
}: {
  label: string;
  value: number | undefined;
  to?: string;
  /** Change against the comparable previous period. Omit where there isn't one. */
  delta?: number;
  deltaTitle?: string;
}) {
  const body = (
    <>
      <p className="text-[11px] font-medium uppercase tracking-wide text-faint">
        {label}
      </p>
      <p className="mt-1 flex items-baseline gap-1.5">
        <span
          className="text-xl font-semibold tabular-nums text-content sm:text-2xl"
          title="Calls where somebody picked up. Attempts that rang out are not counted."
        >
          {value ?? "—"}
        </span>
        {/*
          An arrow and a number, never a colour alone -- and no arrow at all
          when nothing moved, because a flat day is not an event and a grey
          dash on every tile is noise.
        */}
        {delta !== undefined && delta !== 0 ? (
          <span
            title={deltaTitle}
            className={cn(
              "text-[11px] font-medium",
              delta > 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400",
            )}
          >
            {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}
          </span>
        ) : null}
      </p>
    </>
  );

  /* A tile that filters the list is a link, so it opens in a new tab like
     everything else in Pulse. One that only reports a number is not. */
  return to ? (
    <Link
      to={to}
      className="rounded-lg border border-line bg-surface p-2.5 transition-colors hover:border-brand-300 sm:p-3"
    >
      {body}
    </Link>
  ) : (
    <div className="rounded-lg border border-line bg-surface p-2.5 sm:p-3">{body}</div>
  );
}

/**
 * The revenue tile, on both rows.
 *
 * An explicit "not connected" rather than a zero. A zero on a revenue tile
 * reads as "we sold nothing", and somebody would believe it.
 *
 * Not drawn on a phone. Twice down a two-column grid it was a third of a
 * screen saying nothing, each time alone on a row of its own, and pushing the
 * worklist -- the thing a rep opens this for -- further below the fold. It
 * comes back at `sm`, where the grid has room for it beside real numbers.
 */
function RevenueTile({ windowDays }: { windowDays: number | undefined }) {
  return (
    <div
      className="hidden rounded-lg border border-dashed border-line bg-surface p-3 sm:block"
      title={`Orders within ${windowDays ?? 28} days of a call closing, once the fulfilment platform is wired in.`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-faint">
        Revenue
      </p>
      <p className="mt-1 text-sm text-faint">Not connected</p>
    </div>
  );
}

function AttentionColumn({
  title,
  calls,
  currentUserId,
  onClaim,
  onRelease,
  onPin,
  claiming,
}: {
  title: string;
  calls: Parameters<typeof CallRow>[0]["call"][];
  currentUserId: number;
  onClaim: (id: number) => void;
  onRelease: (id: number) => void;
  /** Undefined for anybody but an admin; see the mutation on the board. */
  onPin?: (id: number, pinned: boolean) => void;
  claiming: number | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="border-b border-line px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
        {title}
        <span className="ml-1 tabular-nums">{calls.length}</span>
      </p>
      {calls.length === 0 ? (
        <p className="p-3 text-center text-[11px] text-faint">None</p>
      ) : (
        calls.map((call) => (
          <CallRow
            key={call.id}
            call={call}
            dense
            currentUserId={currentUserId}
            onClaim={onClaim}
            onRelease={onRelease}
            onPin={onPin}
            claiming={claiming === call.id}
            /* The heading above says "Going cold"; the rows need not each say
               it again. See `AttentionBadge`. */
            reasonAlreadySaid
          />
        ))
      )}
    </div>
  );
}

/**
 * One group's calls, collapsed.
 *
 * The list is fetched only when the section is opened. Five groups eagerly
 * fetching their own calls on first paint would be five requests for content
 * behind a disclosure nobody may click -- and the counts in the header come
 * from the board, which is already loaded.
 */
function GroupSection({
  group,
  currentUserId,
  onClaim,
  onRelease,
  onPin,
  claiming,
}: {
  group: SalesGroupCount;
  currentUserId: number;
  onClaim: (id: number) => void;
  onRelease: (id: number) => void;
  /** Undefined for anybody but an admin; see the mutation on the board. */
  onPin?: (id: number, pinned: boolean) => void;
  claiming: number | undefined;
}) {
  /* "Ungrouped" is its own flag rather than a missing groupId: omitting the
     filter would quietly fetch the whole pool into a section labelled with a
     count of eleven. */
  const params =
    group.id === null ? { ungrouped: true } : { groupId: group.id };

  /*
   * Open state in React, not just in the DOM.
   *
   * This used to be `enabled: false` with a manual `refetch()` on toggle,
   * which fetched once and then never again -- so a call claimed by somebody
   * else stayed showing as unclaimed until you collapsed the section and
   * opened it back up. A disabled query also ignores invalidation, so the
   * `sales.claim` broadcast could not reach it either.
   *
   * Tying `enabled` to the open state fixes both: it still fetches nothing
   * until somebody looks, and once they are looking it behaves like every
   * other query on the page.
   */
  const [open, setOpen] = useState(false);

  const calls = useQuery({
    queryKey: queryKeys.salesCalls({ ...params, status: "open", section: true }),
    queryFn: () => salesApi.calls({ ...params, status: "open", limit: 50 }),
    enabled: open,
    staleTime: 15_000,
  });

  return (
    <details
      className="mb-2 rounded-lg border border-line bg-surface"
      onToggle={(event) =>
        setOpen((event.currentTarget as HTMLDetailsElement).open)
      }
    >
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-surface-2">
        <span className="text-sm text-content">{group.name}</span>
        {group.type ? (
          <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] text-faint">
            {group.type}
          </span>
        ) : null}

        <span className="ml-auto flex items-center gap-2 text-[11px] text-faint">
          {group.overdue > 0 ? (
            /* The same duplication the brief asks for: an overdue call is in
               the panel at the top and flagged here in its own group too. */
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 font-medium text-red-800 dark:bg-red-500/15 dark:text-red-300">
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                aria-hidden
              >
                <path d="M12 5v9" />
                <path d="M12 19h.01" />
              </svg>
              {group.overdue} overdue
            </span>
          ) : null}
          {group.unclaimed > 0 ? <span>{group.unclaimed} in the pool</span> : null}
          <span className="tabular-nums">{group.open} open</span>
        </span>
      </summary>

      <div className="border-t border-line">
        {calls.isFetching ? (
          <p className="p-3 text-center text-xs text-faint">Loading…</p>
        ) : (calls.data?.calls.length ?? 0) === 0 ? (
          <p className="p-3 text-center text-xs text-faint">Nothing open here.</p>
        ) : (
          calls.data!.calls.map((call) => (
            <CallRow
              key={call.id}
              call={call}
              dense
              currentUserId={currentUserId}
              onClaim={onClaim}
              onRelease={onRelease}
              onPin={onPin}
              claiming={claiming === call.id}
            />
          ))
        )}
      </div>
    </details>
  );
}
